/**
 * MobileMenu - Menú hamburguesa para dispositivos móviles
 * Muestra un botón flotante que abre el sidebar como overlay
 * Soporta gestos swipe para abrir/cerrar
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { 
  List, X, ChartLine, TrendUp, Lightning, Calculator, Scales,
  ChartPieSlice, ArrowsClockwise, Database, Buildings,
  Briefcase, Newspaper, Bell, Monitor, Pulse, MagnifyingGlass, ChartBar, Globe,
  CalendarBlank, CurrencyCircleDollar, Book, Sparkle
} from '@phosphor-icons/react';

// Herramientas disponibles en móvil - ORDENADAS ALFABÉTICAMENTE (sincronizado con Sidebar3D)
// TOTAL: 30 HERRAMIENTAS FUSIONADAS
const MOBILE_TOOLS = [
  { id: 'alerts', label: 'Centro de Alertas', icon: Bell, color: '#ff6b6b' },
  { id: 'analyzer', label: 'Analizador de Activos', icon: MagnifyingGlass, color: '#f59e0b' },
  { id: 'asset360', label: 'Análisis Bursátil 360', icon: Sparkle, color: '#E8C55C' },
  { id: 'proAnalyst', label: 'Analista Pro', icon: Sparkle, color: '#ffd700' },
  { id: 'indicadoresPro', label: 'Análisis Técnico Profesional', icon: ChartLine, color: '#06b6d4' },
  { id: 'ebitdaAnalysis', label: 'Análisis Margen EBITDA', icon: ChartBar, color: '#0ea5e9' },
  { id: 'rendimiento', label: 'Análisis de Rendimiento', icon: TrendUp, color: '#00ff6a' },
  { id: 'categories', label: 'Análisis de Sectores', icon: ChartPieSlice, color: '#06b6d4' },
  { id: 'signals', label: 'Análisis Señales Trading', icon: Lightning, color: '#ff9500' },
  { id: 'backtesting', label: 'Backtesting', icon: Database, color: '#3b82f6' },
  { id: 'backup', label: 'Backup', icon: Database, color: '#22c55e' },
  { id: 'marketDatabase', label: 'Base de Datos', icon: Database, color: '#06b6d4' },
  { id: 'stockScreener', label: 'Buscador Acciones', icon: MagnifyingGlass, color: '#06b6d4' },
  { id: 'calculatorCenter', label: 'Calculadoras PRO', icon: Calculator, color: '#00dcff' },
  { id: 'economicCalendar', label: 'Calendario Económico', icon: CalendarBlank, color: '#f59e0b' },
  { id: 'fullscreen', label: 'Cartera Detallada', icon: Monitor, color: '#ffd700' },
  { id: 'livePortfolio', label: 'Cartera en Vivo', icon: Pulse, color: '#00ff6a' },
  { id: 'heatmapCenter', label: 'Centro de Heatmaps', icon: ChartPieSlice, color: '#a855f7' },
  { id: 'historicalCenter', label: 'Centro de Históricos', icon: Database, color: '#8b5cf6' },
  { id: 'universalComparator', label: 'Comparador de Activos', icon: Scales, color: '#a855f7' },
  { id: 'contabilidad', label: 'Contabilidad', icon: Calculator, color: '#10b981' },
  { id: 'financialDatabase', label: 'Datos Financieros', icon: Database, color: '#a855f7' },
  { id: 'dividendTracker', label: 'Dividendos', icon: CurrencyCircleDollar, color: '#22c55e' },
  { id: 'buystrategy', label: 'Estrategia de Compra', icon: Briefcase, color: '#10b981' },
  { id: 'portfolio', label: 'Mi Portafolio', icon: Briefcase, color: '#a855f7' },
  { id: 'multiChart', label: 'Multi-Gráfico', icon: ChartBar, color: '#f59e0b' },
  { id: 'news', label: 'Noticias', icon: Newspaper, color: '#00dcff' },
  { id: 'financialGoals', label: 'Objetivos Financieros', icon: TrendUp, color: '#a855f7' },
  { id: 'manual', label: 'Manual', icon: Book, color: '#22d3ee' },
];

// Configuración de gestos swipe
const SWIPE_THRESHOLD = 50; // Mínimo de píxeles para detectar swipe
const EDGE_ZONE = 30; // Zona del borde izquierdo para iniciar swipe

const MobileMenu = ({ onSectionChange, newsCount = 0, alertsCount = 0, onOpenAlertsPanel }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [swipeProgress, setSwipeProgress] = useState(0); // 0-100 para animación de swipe
  
  // Referencias para el tracking de swipe
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const isSwiping = useRef(false);
  const swipeDirection = useRef(null); // 'open' o 'close'

  // Detectar si es móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Cerrar menú al cambiar de tamaño a escritorio
  useEffect(() => {
    if (!isMobile && isOpen) {
      setIsOpen(false);
    }
  }, [isMobile, isOpen]);

  // Bloquear scroll del body cuando el menú está abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Gestos swipe para abrir el menú desde el borde izquierdo
  useEffect(() => {
    if (!isMobile) return;

    const handleTouchStart = (e) => {
      const touch = e.touches[0];
      touchStartX.current = touch.clientX;
      touchStartY.current = touch.clientY;
      
      // Solo iniciar swipe para abrir si está en la zona del borde izquierdo
      if (!isOpen && touch.clientX <= EDGE_ZONE) {
        isSwiping.current = true;
        swipeDirection.current = 'open';
      }
    };

    const handleTouchMove = (e) => {
      if (!isSwiping.current) return;
      
      const touch = e.touches[0];
      const deltaX = touch.clientX - touchStartX.current;
      const deltaY = Math.abs(touch.clientY - touchStartY.current);
      
      // Cancelar si es más vertical que horizontal
      if (deltaY > Math.abs(deltaX) * 1.5) {
        isSwiping.current = false;
        setSwipeProgress(0);
        return;
      }

      if (swipeDirection.current === 'open' && deltaX > 0) {
        // Abriendo: progreso de 0 a 100
        const progress = Math.min(100, (deltaX / 200) * 100);
        setSwipeProgress(progress);
      }
    };

    const handleTouchEnd = () => {
      if (!isSwiping.current) return;
      
      // Si el progreso supera el umbral, completar la acción
      if (swipeProgress > 30 && swipeDirection.current === 'open') {
        setIsOpen(true);
      }
      
      // Reset
      isSwiping.current = false;
      swipeDirection.current = null;
      setSwipeProgress(0);
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isMobile, isOpen, swipeProgress]);

  const handleToolClick = useCallback((toolId) => {
    // Caso especial para alertas
    if (toolId === 'alerts' && onOpenAlertsPanel) {
      onOpenAlertsPanel();
    } else if (onSectionChange) {
      onSectionChange(toolId);
    }
    setIsOpen(false);
  }, [onSectionChange, onOpenAlertsPanel]);

  // Swipe para cerrar el menú
  const handlePanelTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
    isSwiping.current = true;
    swipeDirection.current = 'close';
  }, []);

  const handlePanelTouchMove = useCallback((e) => {
    if (!isSwiping.current || swipeDirection.current !== 'close') return;
    
    const touch = e.touches[0];
    const deltaX = touchStartX.current - touch.clientX;
    const deltaY = Math.abs(touch.clientY - touchStartY.current);
    
    // Cancelar si es más vertical que horizontal
    if (deltaY > Math.abs(deltaX) * 1.5) {
      isSwiping.current = false;
      setSwipeProgress(0);
      return;
    }

    if (deltaX > 0) {
      // Cerrando: progreso de 0 a 100
      const progress = Math.min(100, (deltaX / 200) * 100);
      setSwipeProgress(progress);
    }
  }, []);

  const handlePanelTouchEnd = useCallback(() => {
    if (!isSwiping.current) return;
    
    // Si el progreso supera el umbral, cerrar
    if (swipeProgress > 30 && swipeDirection.current === 'close') {
      setIsOpen(false);
    }
    
    // Reset
    isSwiping.current = false;
    swipeDirection.current = null;
    setSwipeProgress(0);
  }, [swipeProgress]);

  // Solo mostrar en móvil
  if (!isMobile) return null;

  // Calcular transform del panel basado en swipe progress
  const panelTransform = isOpen && swipeProgress > 0 
    ? `translateX(-${swipeProgress}%)` 
    : swipeProgress > 0 && !isOpen 
      ? `translateX(${-100 + swipeProgress}%)` 
      : undefined;

  return (
    <>
      {/* Indicador de zona de swipe (sutil) */}
      {!isOpen && (
        <div 
          className="fixed left-0 top-1/2 -translate-y-1/2 w-1 h-20 rounded-r-full opacity-30 z-40 md:hidden"
          style={{
            background: 'linear-gradient(180deg, transparent 0%, #00dcff 50%, transparent 100%)',
            boxShadow: '0 0 10px rgba(0, 220, 255, 0.5)',
          }}
        />
      )}

      {/* Botón hamburguesa flotante */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 left-4 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-lg md:hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(0, 220, 255, 0.9) 0%, rgba(0, 150, 200, 0.9) 100%)',
          boxShadow: '0 4px 20px rgba(0, 220, 255, 0.4), 0 0 30px rgba(0, 220, 255, 0.2)',
          border: '2px solid rgba(255, 255, 255, 0.2)',
        }}
        data-testid="mobile-menu-button"
        aria-label="Abrir menú de herramientas"
      >
        <List size={28} weight="bold" className="text-white" />
      </button>

      {/* Preview del panel durante swipe (cuando está cerrado) */}
      {!isOpen && swipeProgress > 0 && createPortal(
        <div 
          className="fixed inset-0 z-[9998] md:hidden pointer-events-none"
        >
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            style={{ opacity: swipeProgress / 100 }}
          />
          <div 
            className="absolute left-0 top-0 h-full w-72"
            style={{
              background: 'linear-gradient(180deg, rgba(8, 20, 38, 0.98) 0%, rgba(5, 12, 25, 0.99) 100%)',
              borderRight: '2px solid rgba(0, 255, 255, 0.3)',
              transform: `translateX(${-100 + swipeProgress}%)`,
            }}
          />
        </div>,
        document.body
      )}

      {/* Overlay del menú */}
      {isOpen && createPortal(
        <div 
          className="fixed inset-0 z-[9999] md:hidden"
          onClick={() => setIsOpen(false)}
        >
          {/* Fondo oscuro */}
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            style={{ 
              animation: 'fadeIn 0.2s ease-out',
              opacity: swipeProgress > 0 ? 1 - (swipeProgress / 100) : 1,
            }}
          />

          {/* Panel del menú */}
          <div 
            className="absolute left-0 top-0 h-full w-72 overflow-y-auto"
            style={{
              background: 'linear-gradient(180deg, rgba(8, 20, 38, 0.98) 0%, rgba(5, 12, 25, 0.99) 100%)',
              borderRight: '2px solid rgba(0, 255, 255, 0.3)',
              boxShadow: '4px 0 30px rgba(0, 0, 0, 0.5)',
              animation: swipeProgress === 0 ? 'slideInLeft 0.3s ease-out' : undefined,
              transform: panelTransform,
            }}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handlePanelTouchStart}
            onTouchMove={handlePanelTouchMove}
            onTouchEnd={handlePanelTouchEnd}
            data-testid="mobile-menu-panel"
          >
            {/* Header del menú */}
            <div 
              className="sticky top-0 p-4 border-b flex items-center justify-between z-10"
              style={{ 
                borderColor: 'rgba(0, 255, 255, 0.15)',
                background: 'linear-gradient(180deg, rgba(8, 20, 38, 1) 0%, rgba(8, 20, 38, 0.95) 100%)',
              }}
            >
              <div className="flex items-center gap-2">
                <Pulse size={22} className="text-gold-400" style={{ filter: 'drop-shadow(0 0 6px #00dcff)' }} />
                <span 
                  className="font-bold text-base tracking-wider"
                  style={{ color: '#00dcff', textShadow: '0 0 10px rgba(0, 220, 255, 0.5)' }}
                >
                  HERRAMIENTAS
                </span>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-lg hover:bg-gold-500/10 transition-colors"
                style={{ color: '#00dcff' }}
                aria-label="Cerrar menú"
              >
                <X size={24} />
              </button>
            </div>

            {/* Indicador de swipe para cerrar */}
            <div 
              className="flex justify-center py-2 text-gray-600 text-xs"
              style={{ borderBottom: '1px solid rgba(0, 220, 255, 0.1)' }}
            >
              <span>← Desliza para cerrar</span>
            </div>

            {/* Lista de herramientas */}
            <div className="p-3 space-y-1">
              {MOBILE_TOOLS.map((tool) => {
                const IconComponent = tool.icon;
                const hasNotification = (tool.id === 'news' && newsCount > 0) || 
                                        (tool.id === 'alerts' && alertsCount > 0);
                
                return (
                  <button
                    key={tool.id}
                    onClick={() => handleToolClick(tool.id)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl transition-all duration-200 text-left group"
                    style={{
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(0,0,0,0.2) 100%)',
                      border: '1px solid rgba(255,255,255,0.05)',
                    }}
                    data-testid={`mobile-tool-${tool.id}`}
                  >
                    {/* Icono */}
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-transform group-active:scale-95"
                      style={{ 
                        background: `linear-gradient(145deg, ${tool.color}30 0%, ${tool.color}10 100%)`,
                        border: `1px solid ${tool.color}40`,
                        boxShadow: `0 2px 10px ${tool.color}20`
                      }}
                    >
                      <IconComponent 
                        size={22} 
                        style={{ 
                          color: tool.color,
                          filter: `drop-shadow(0 0 4px ${tool.color})`
                        }} 
                      />
                    </div>
                    
                    {/* Texto */}
                    <span className="text-sm font-medium text-gray-200 flex-1">
                      {tool.label}
                    </span>

                    {/* Badge de notificación */}
                    {hasNotification && (
                      <span 
                        className="px-2 py-0.5 text-xs font-bold rounded-full"
                        style={{
                          background: tool.id === 'alerts' ? '#ff6b6b' : '#00dcff',
                          color: '#fff',
                          boxShadow: `0 0 10px ${tool.id === 'alerts' ? '#ff6b6b' : '#00dcff'}50`
                        }}
                      >
                        {tool.id === 'news' ? newsCount : alertsCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Footer */}
            <div 
              className="sticky bottom-0 p-4 border-t text-center"
              style={{ 
                borderColor: 'rgba(0, 255, 255, 0.15)',
                background: 'linear-gradient(180deg, rgba(8, 20, 38, 0.95) 0%, rgba(8, 20, 38, 1) 100%)',
              }}
            >
              <p className="text-xs text-gray-500">
                KAPITAL - Análisis Técnico
              </p>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Estilos de animación */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideInLeft {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
};

export default MobileMenu;
